import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Search, Loader2, Landmark, BookMarked, Clock } from "lucide-react";

const SEARCH_URL = `${process.env.REACT_APP_BACKEND_URL}/api/directory/search`;

const sourceMeta = {
  rolodex: { Icon: BookMarked, label: "Rolodex", color: "#C9A227" },
  saved: { Icon: Clock, label: "Recently faxed", color: "#6C8CD5" },
  fdic: { Icon: Landmark, label: "FDIC bank", color: "#3DBB8E" },
};

// Reusable counterparty/lender search. Queries the merged directory (rolodex +
// saved fax book + live FDIC bank lookup) and calls onSelect with {name,address,fax}.
export const CounterpartyAutocomplete = ({ onSelect, placeholder = "Search lender / bank / servicer…", testid = "counterparty-search", theme = "dark" }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(SEARCH_URL, { params: { q: q.trim() } });
        setResults(r.data.results || []);
        setOpen(true);
      } catch { setResults([]); }
      setLoading(false);
    }, 280);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  const pick = (r) => {
    onSelect && onSelect({ ...r, name: r.name || "", address: r.address || "", fax: r.fax || "" });
    setQ("");
    setResults([]);
    setOpen(false);
  };

  const dark = theme === "dark";
  const inputCls = dark
    ? "w-full bg-[#0F0F0A] text-[#ECECE4] border border-[#2A2A22] focus:border-[#C9A227] rounded pl-8 pr-3 py-2 text-[13px] outline-none"
    : "w-full bg-white text-black border border-[#D8D8D0] focus:border-[#B8860B] rounded pl-8 pr-3 py-2 text-[13px] outline-none";

  return (
    <div className="relative" ref={boxRef} data-testid={`${testid}-wrap`}>
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A8A8A]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder}
          data-testid={`${testid}-input`}
          className={inputCls}
        />
        {loading && <Loader2 size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-[#8A8A8A]" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-[#2A2A22] bg-[#141410] shadow-xl" data-testid={`${testid}-results`}>
          {results.map((r, i) => {
            const m = sourceMeta[r.source] || sourceMeta.fdic;
            return (
              <button
                key={`${r.source}-${r.name}-${i}`}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                data-testid={`${testid}-opt-${i}`}
                className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-[#1F1F18] last:border-0"
              >
                <div className="flex items-center gap-2">
                  <m.Icon size={13} style={{ color: m.color }} className="shrink-0" />
                  <span className="text-[12px] text-[#ECECE4] font-medium truncate">{r.name}</span>
                  {r.fax ? (
                    <span className="ml-auto text-[10px] font-mono text-[#5fd3a8] shrink-0">{r.confirmed ? "✓ " : ""}{r.fax}</span>
                  ) : (
                    <span className="ml-auto text-[9px] uppercase tracking-wide text-[#8A8A8A] shrink-0">no fax</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 pl-[21px]">
                  <span className="text-[10px] uppercase tracking-[0.1em]" style={{ color: m.color }}>{m.label}</span>
                  {r.routing ? <span className="text-[10px] text-[#8A6D1B]">· ABA {r.routing}</span> : null}
                  {r.address ? <span className="text-[10px] text-[#8A8A8A] truncate">· {r.address}</span> : null}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CounterpartyAutocomplete;
